import {
  Button,
  ButtonWithLoader,
  Col,
  dateUtils,
  Form,
  InputGroup,
  Row,
  Wizard,
} from '@valid-eval/shared-react-components';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Date from 'components/Form/Date';
import Email from 'components/Form/Email';
import FileInput from 'components/Form/FileInput';
import HelpText from 'components/Form/HelpText';
import Input from 'components/Form/Input';
import TextArea from 'components/Form/TextArea';
import { blankNumberFormat, blankNumberNormalize } from 'components/FormFields/utils';
import { minLength, minValue, name, number, slug } from 'components/FormFields/validations';
import ServerImage from 'components/Image';
import { getSupportEmail } from 'config';
import { useUpdateEvent } from 'data/features/eventsHooks';
import { Event } from 'data/features/eventsTypes';
import { useDebouncedCallback } from 'utils/debounce-promise';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';

import { EventStepComponentProps } from '.';
import EditEventStepForm from './components/EditEventStepForm';
import { validateTeamSignupDeadline, validateTeamSignupOpen } from './components/utils';

const { formValueToIso, isoDateToFormValue } = dateUtils;

function EventStepComponent({ event, label, subLabel, nextStep }: EventStepComponentProps) {
  const { t } = useTranslation();
  const { update, saving } = useUpdateEvent();
  const showErrorIfRejected = useShowErrorIfRejected();

  const email = getSupportEmail();
  const areDatesDisabled = !event?.start_date || !event?.end_date;
  const currentTimezone = moment.tz.guess();
  const timezone = moment.tz(currentTimezone).format('Z');
  const startDate = moment(event?.start_date).tz(currentTimezone);
  const endDate = moment(event?.end_date).tz(currentTimezone);

  const teamSignupOpenValidation = (value: string) => {
    return validateTeamSignupOpen(
      value,
      methods.getValues().team_signup_deadline,
      startDate,
      endDate,
    );
  };

  const teamSignupDeadlineValidation = (value: string) => {
    return validateTeamSignupDeadline(
      value,
      methods.getValues().team_signup_open,
      startDate,
      endDate,
    );
  };

  const methods = useForm<Partial<Event & { new_logo_url: string | null }>>({
    mode: 'onBlur',
    defaultValues: {
      id: event.id,
      logo_url: event.logo_url,
      new_logo_url: null,
      name: event.name,
      description: event.description,
      slug: event.slug,
      aggregate_amount: event.aggregate_amount || 0,
      team_signup_open: isoDateToFormValue(event.team_signup_open),
      team_signup_deadline: isoDateToFormValue(event.team_signup_deadline),
      reply_to_email: event.reply_to_email,
      reply_to_name: event.reply_to_name,
      award_date: isoDateToFormValue(event.award_date),
      number: event.number,
    },
  });

  // values transformation before saving
  const transformValues = (data: Partial<Event>) => {
    return {
      ...data,
      team_signup_open: formValueToIso(data.team_signup_open),
      team_signup_deadline: formValueToIso(data.team_signup_deadline),
      award_date: formValueToIso(data.award_date),
    } as Event;
  };

  // debounced save callback
  const debouncedSave = useDebouncedCallback(
    async (data: Partial<Event>) => {
      const errorKeys = Object.keys(methods.formState.errors);
      for (const errorKey of errorKeys) {
        data[errorKey as keyof Event] = undefined;
      }
      save(data);
    },
    [methods.formState.errors],
    500,
  );

  const handleNextStep = async () => {
    await methods.handleSubmit(save)();
    nextStep();
  };

  // Submit form when values change
  useEffect(() => {
    const subscription = methods.watch((_, { type }) => {
      if (type !== 'change') return;
      methods.trigger();
      debouncedSave(methods.getValues());
    });
    return subscription.unsubscribe;
  }, [methods.watch]);

  const save = async (data: Partial<Event>) => {
    const [result, hasError] = showErrorIfRejected(await update(transformValues(data)));
    if (!hasError) {
      // Update the logo image after the update
      methods.setValue('logo_url', result.payload?.event?.logo_url);
      methods.trigger();
    }
  };

  const formValues = methods.getValues();

  return (
    <EditEventStepForm methods={methods} onSubmit={save} label={label} subLabel={subLabel}>
      <>
        <Row className="mt-3 mb-3">
          <Col md="auto">
            <ServerImage
              key={formValues.logo_url}
              src={formValues.logo_url ?? '/images/no-image.png'}
              width={120}
              height={120}
              fit
            />
          </Col>
          <Col>
            <Form.Label>{t('event.form.logo')}</Form.Label>
            <HelpText text={t('event.form.logo_help')} />
            <FileInput
              name="new_logo_url"
              id="new_logo_url"
              acceptedFileType=".png, .jpg, .jpeg"
              onFinishUpload={(_, url) => {
                methods.setValue('new_logo_url', url);
                methods.trigger();
                debouncedSave(methods.getValues());
              }}
              onNoSession={() => {}}
              signingUrl={`/api/v1/events/${event.id}/upload_logo_signed_url`}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={12}>
            <Input name="name" id="name" label={t('event.form.name')} required autoFocus />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={12}>
            <TextArea
              name="description"
              id="description"
              label={t('event.form.description')}
              required
              rules={{ validate: { minLength: minLength(100) } }}
            />
            <HelpText text={t('event.form.description_help')} />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={6}>
            <Input
              name="slug"
              id="slug"
              label={t('event.form.slug')}
              required
              rules={{ validate: { slug } }}
              placeholder={t('event.form.slug_placeholder')}
            />
            <HelpText text={t('event.form.slug_help')} />
          </Col>
          <Col md={6}>
            <Input
              name="aggregate_amount"
              id="aggregate_amount"
              label={t('event.form.aggregate_amount')}
              required
              format={blankNumberFormat}
              normalize={blankNumberNormalize}
              rules={{ validate: { minValue: minValue(0), number } }}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={6}>
            <Date
              name="team_signup_open"
              id="team_signup_open"
              label={t('event.form.team_signup_open')}
              placeholder={t('common.date_time_placeholder')}
              isDateTime
              after={
                <InputGroup.Text className="overflow-hidden">
                  <small className="text-truncate">
                    {t('event.form.timezone_suffix', { timezone })}
                  </small>
                </InputGroup.Text>
              }
              rules={{ validate: { teamSignupOpen: teamSignupOpenValidation } }}
              disabled={areDatesDisabled}
            />
            <HelpText text={t('event.form.team_signup_open_help')} />
          </Col>
          <Col md={6}>
            <Date
              name="team_signup_deadline"
              id="team_signup_deadline"
              label={t('event.form.team_signup_deadline')}
              placeholder={t('common.date_time_placeholder')}
              isDateTime
              after={
                <InputGroup.Text className="overflow-hidden">
                  <small className="text-truncate">
                    {t('event.form.timezone_suffix', { timezone })}
                  </small>
                </InputGroup.Text>
              }
              rules={{ validate: { teamSignupDeadline: teamSignupDeadlineValidation } }}
              disabled={areDatesDisabled}
            />
            <HelpText text={t('event.form.team_signup_deadline_help')} />
          </Col>
        </Row>
        {areDatesDisabled && (
          <Row>
            <Col>
              <div className="alert alert-warning d-flex align-items-center justify-content-center">
                <i className="fas fa-exclamation-circle me-2 text-orange01 me-3" />
                <span>{t('settings.error.team_signup_window_disabled', { email })}</span>
              </div>
            </Col>
          </Row>
        )}
        <Row className="mb-2">
          <Col md={6}>
            <Email
              name="reply_to_email"
              id="reply_to_email"
              label={t('event.form.reply_to_email')}
            />
            <HelpText text={t('event.form.reply_to_email_help')} />
          </Col>
          <Col md={6}>
            <Input
              name="reply_to_name"
              id="reply_to_name"
              label={t('event.form.reply_to_name')}
              rules={{
                validate: {
                  name,
                },
              }}
            />
            <HelpText text={t('event.form.reply_to_name_help')} />
          </Col>
        </Row>
        <Row className="mb-2">
          <Col md={7}>
            <Date
              name="award_date"
              id="award_date"
              label={t('event.form.award_date')}
              placeholder={t('common.date_time_placeholder')}
              isDateTime
              placement="top"
              after={
                <InputGroup.Text className="overflow-hidden">
                  <small className="text-truncate">
                    {t('event.form.timezone_suffix', { timezone })}
                  </small>
                </InputGroup.Text>
              }
            />
          </Col>
          <Col md={5}>
            <Input name="number" id="number" label={t('event.form.number')} />
          </Col>
        </Row>

        <Wizard.Footer>
          <div className="d-flex justify-content-end align-items-center">
            <Button variant="secondary" className="me-3" type="submit" disabled={saving}>
              {t('event.form.save_progress')}
            </Button>
            <ButtonWithLoader
              variant="primary"
              type="button"
              loading={saving}
              onClick={handleNextStep}
            >
              {t('event.form.next')}
            </ButtonWithLoader>
          </div>
        </Wizard.Footer>
      </>
    </EditEventStepForm>
  );
}

export default EventStepComponent;
